import { ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';

export const TermsConditions = ({ onClose }) => {
  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {onClose && (
          <Button
            onClick={onClose}
            variant="outline"
            className="mb-6"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Home
          </Button>
        )}

        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms and Conditions</h1>
          <p className="text-gray-600 mb-8">Last updated: December 19, 2025</p>

          <div className="space-y-8 text-gray-700 leading-relaxed">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p>
                Welcome to TOR1 Services. These Terms and Conditions ("Terms") govern your use of our website and Transfer of Residence (TOR1) services. By accessing our website or using our services, you agree to be bound by these Terms.
              </p>
              <p className="mt-3">
                If you do not agree with any part of these Terms, please do not use our website or services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Definitions</h2>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>"We", "Us", "Our"</strong> refers to TOR1 Services</li>
                <li><strong>"You", "Your", "Client"</strong> refers to the user of our website or services</li>
                <li><strong>"Services"</strong> refers to Transfer of Residence (TOR1) application processing, customs documentation, and related relocation services</li>
                <li><strong>"TOR1"</strong> refers to Transfer of Residence customs relief as defined by HMRC</li>
                <li><strong>"Website"</strong> refers to our online platform at import-duty-waiver.preview.emergentagent.com</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Use of Website</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">3.1 Acceptable Use</h3>
              <p className="mb-3">You agree to use our website only for lawful purposes and in accordance with these Terms. You must not:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Use the website in any way that violates any applicable law or regulation</li>
                <li>Engage in any fraudulent or deceptive activities</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Transmit any viruses, malware, or harmful code</li>
                <li>Interfere with or disrupt the website or servers</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">3.2 Account Responsibility</h3>
              <p>
                You are responsible for maintaining the confidentiality of any login credentials and for all activities that occur under your account.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Our Services</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">4.1 Service Description</h3>
              <p className="mb-3">We provide the following services:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>TOR1 Transfer of Residence application processing</li>
                <li>Customs documentation preparation and submission</li>
                <li>Eligibility assessment and consultation</li>
                <li>Coordination with HMRC and UK Border Force</li>
                <li>Relocation support and guidance</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">4.2 Service Limitations</h3>
              <p className="mb-3">Our services are subject to the following limitations:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Services are available only for UK nationals or individuals with right of abode in the UK</li>
                <li>Eligibility for TOR1 relief is determined by HMRC regulations</li>
                <li>We cannot guarantee approval of TOR1 applications by UK authorities</li>
                <li>Processing times depend on HMRC and UK Border Force workload</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">4.3 Client Responsibilities</h3>
              <p className="mb-3">You agree to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide accurate and complete information</li>
                <li>Submit all required documentation in a timely manner</li>
                <li>Meet all eligibility requirements for TOR1 relief</li>
                <li>Comply with all HMRC and UK customs regulations</li>
                <li>Inform us immediately of any changes to your circumstances</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Fees and Payment</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">5.1 Service Fees</h3>
              <p>
                Our service fees will be communicated to you during the consultation process. Fees are based on the complexity of your relocation and the services required.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">5.2 Payment Terms</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Payment terms will be specified in your service agreement</li>
                <li>Fees are typically due before commencement of application processing</li>
                <li>We accept payment via bank transfer or other agreed methods</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">5.3 Refund Policy</h3>
              <p className="mb-3">
                Refunds are considered on a case-by-case basis. If your TOR1 application is rejected due to ineligibility that we failed to identify during our assessment, we will provide a partial refund as agreed in your service contract. No refunds are provided for:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Applications rejected due to incorrect information provided by the client</li>
                <li>Changes in HMRC regulations or policies</li>
                <li>Services already rendered at the time of cancellation</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Intellectual Property</h2>
              <p>
                All content on this website, including text, graphics, logos, images, and software, is the property of TOR1 Services or its licensors and is protected by UK and international copyright laws. You may not reproduce, distribute, or create derivative works without our express written permission.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Disclaimers</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">7.1 No Guarantee of Results</h3>
              <p>
                While we strive to provide excellent service and maximize your chances of TOR1 approval, we cannot guarantee that your application will be approved by HMRC. The final decision rests with UK government authorities.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">7.2 Information Accuracy</h3>
              <p>
                We make every effort to ensure information on our website is accurate and up-to-date. However, HMRC regulations and customs procedures may change. We are not liable for any decisions made based on outdated information.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">7.3 Third-Party Services</h3>
              <p>
                We may recommend or coordinate with third-party service providers (e.g., removal companies, freight forwarders). We are not responsible for the actions or services of these third parties.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Limitation of Liability</h2>
              <p className="mb-3">
                To the fullest extent permitted by law, TOR1 Services shall not be liable for:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Rejection of TOR1 applications by HMRC or UK Border Force</li>
                <li>Delays in processing by government authorities</li>
                <li>Changes in customs regulations or import duty rates</li>
                <li>Loss or damage to goods during transit (this is the responsibility of your removal company)</li>
                <li>Indirect, consequential, or incidental damages</li>
              </ul>
              <p className="mt-3">
                Our total liability to you for any claims arising from our services shall not exceed the fees paid for those services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless TOR1 Services from any claims, damages, or expenses arising from:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-3">
                <li>Your violation of these Terms</li>
                <li>Provision of false or misleading information</li>
                <li>Your violation of any laws or regulations</li>
                <li>Your infringement of third-party rights</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Confidentiality</h2>
              <p>
                We will maintain the confidentiality of your personal information and documentation in accordance with our Privacy Policy and applicable data protection laws. You agree to keep confidential any proprietary information about our processes and methodologies.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Termination</h2>
              <p className="mb-3">
                We reserve the right to terminate or suspend your access to our services at any time if:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>You breach these Terms</li>
                <li>You provide false or fraudulent information</li>
                <li>Payment obligations are not met</li>
                <li>We determine you are ineligible for TOR1 relief</li>
              </ul>
              <p className="mt-3">
                You may terminate our services by providing written notice. Any fees for services already rendered remain payable.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Force Majeure</h2>
              <p>
                We shall not be liable for any failure or delay in performing our obligations due to circumstances beyond our reasonable control, including but not limited to natural disasters, war, strikes, government actions, pandemics, or technical failures.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Governing Law and Jurisdiction</h2>
              <p>
                These Terms are governed by the laws of England and Wales. Any disputes arising from these Terms or our services shall be subject to the exclusive jurisdiction of the courts of England and Wales.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. Changes will be posted on this page with an updated "Last updated" date. Your continued use of our services after changes constitutes acceptance of the modified Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Severability</h2>
              <p>
                If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Entire Agreement</h2>
              <p>
                These Terms, together with our Privacy Policy and any service agreement, constitute the entire agreement between you and TOR1 Services regarding the use of our website and services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">17. Contact Information</h2>
              <p className="mb-3">For questions about these Terms and Conditions, please contact us:</p>
              <div className="bg-blue-50 p-6 rounded-lg">
                <p className="font-semibold text-gray-900 mb-2">TOR1 Services</p>
                <p>Email: <a href="mailto:info@tor1services.co.uk" className="text-blue-900 hover:underline">info@tor1services.co.uk</a></p>
                <p>Phone: <a href="tel:0034699913354" className="text-blue-900 hover:underline">0034 699 913 354</a></p>
              </div>
            </section>

            <section className="bg-yellow-50 p-6 rounded-lg mt-8">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Important Notice</h3>
              <p className="text-gray-700">
                By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. If you have any questions or concerns, please contact us before proceeding with our services.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};
